/* The score is the cockpit's own: the plan-quality summary already scores every offering out of
   the payload the tile is drawn from, so the section reads that number rather than computing a
   second one that could disagree with the plan-quality tile two rows above it.

   The review is the partner page's own dialog — the one the commitment widget opens — because
   justifying a weak plan is its job, not this card's. This file is the single place the tile
   reaches for it, and it opens on the offering the section is stated for. */
import cn from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getQualityBand } from 'partner360/components/qualityIndicator'
import { FINANCIAL_SECTION_URL_VALUE, TAB_URL } from 'partner360/configs'
import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_BITS,
  getAttentionStatusRole,
  type TAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import type {
  IFinancialPlanQualityCategoryScore,
  IFinancialPlanQualityPillar,
  TFinancialPlanQualityValueUnit,
} from 'partner360/features/financialPlanQuality'
import { PlanReviewDialog, type TPlanReviewRole } from 'partner360/features/planReview'
import { scrollToElement } from 'partner360/helpers/scrollToElement'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { EMPTY_COCKPIT_VALUE } from '../../../../lib'
import {
  selectCockpitFinancialPlanQualitySummary,
  selectCockpitPartnerData,
  selectCockpitUserAccess,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { CockpitTable, type ICockpitTableColumn } from '../../../components/cockpitTable'
import { PlanQualityBadge } from '../../../components/planQualityBadge'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusFinSobQuality.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planQuality
const FIN_SOB_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.finSobQuality
const DETAIL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail
const TABLE_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob

/* Keys this section needs and `configs/translations.ts` does not carry yet. They follow the
   registry's own namespace, so moving them there is a cut and a paste. */
/* `FinSob Plan is not good`. The bit catalog names only the bits it already has a section for,
   and this one is added to it with the wiring rather than from here. */
/* The line a plan is expected to pass, as the sheet states it. It is the cut `getQualityBand`
   makes between `Moderate` and `High`, and that cut is passed rather than matched: a score of
   exactly the threshold is still `Moderate`, which is the reading `isBelowHighQuality` takes in
   the plan review. The plate's wording is therefore read off the band rather than off the
   subtraction, so the sentence and the badges below it cannot say different things. */
const QUALITY_THRESHOLD = 80

/* The review dialog names its audiences with the commitment's own three words; the sheet's four
   roles map onto them, and everyone the sheet does not name reads the review as a manager does. */
const PLAN_REVIEW_ROLE_BY_STATUS_ROLE: Record<TAttentionStatusRole, TPlanReviewRole> = {
  pbm: 'hp',
  manager: 'manager',
  partner: 'partner',
  other: 'manager',
}

/** What the plate says about the score: the plan passes the line, sits exactly on it — which is
 *  still short of it — or is a stated number of points below it. */
const getStandingKey = (isAboveThreshold: boolean, gap: number) => {
  if (isAboveThreshold) {
    return FIN_SOB_KEYS.met
  }

  return gap > 0 ? FIN_SOB_KEYS.gap : FIN_SOB_KEYS.atThreshold
}

/** A growth figure the way the plan-quality table states it: signed, to one decimal, in its
 *  pillar's own unit — percent for revenue, percentage points for SOB. */
const formatValue = (value: number | null, valueUnit: TFinancialPlanQualityValueUnit) => {
  if (value === null) {
    return EMPTY_COCKPIT_VALUE
  }

  return `${value > 0 ? '+' : ''}${value.toFixed(1)}${valueUnit === 'percent' ? '%' : ' pp'}`
}

const getCategoryColumns = (
  valueUnit: TFinancialPlanQualityValueUnit
): ICockpitTableColumn<IFinancialPlanQualityCategoryScore>[] => [
  {
    key: 'category',
    header: <Translation span text={TABLE_KEYS.category} />,
    renderCell: ({ category }) => category,
  },
  {
    key: 'planned',
    header: <Translation span text={KEYS.yoy} />,
    renderCell: ({ planned }) => formatValue(planned, valueUnit),
  },
  {
    key: 'target',
    header: <Translation span text={KEYS.marketTarget} />,
    renderCell: ({ target }) => formatValue(target, valueUnit),
  },
  {
    key: 'points',
    header: <Translation span text={KEYS.score} />,
    renderCell: ({ points }) => <PlanQualityBadge score={points} pill />,
  },
]

interface IQualityPillarProps {
  titleKey: string
  pillar: IFinancialPlanQualityPillar
  columns: ICockpitTableColumn<IFinancialPlanQualityCategoryScore>[]
  emptyTextKey: string
}

/* One pillar of the score: what it stands at, and the categories the average is taken over. Only
   the categories themselves are listed — their sub-categories are the planning section's own
   depth, and the card states what the score is made of rather than the whole plan. */
const QualityPillar = ({ titleKey, pillar, columns, emptyTextKey }: IQualityPillarProps) => (
  <section className={styles.pillar}>
    <div className={styles.pillarHead}>
      <span className={styles.pillarTitle}>
        <Translation span text={titleKey} />
      </span>
      <PlanQualityBadge score={pillar.score} pill />
    </div>

    {pillar.categories.length === 0 ? (
      <p className={styles.empty}>
        <Translation span text={emptyTextKey} />
      </p>
    ) : (
      <CockpitTable columns={columns} items={pillar.categories} getItemKey={({ id }) => id} />
    )}
  </section>
)

QualityPillar.displayName = 'QualityPillar'

/**
 * The quality detail of the `FinSob Plan is not good` status: where the offering's plan stands
 * against the quality a plan is expected to reach, and the two pillars — revenue growth and SOB
 * growth — the number is averaged from, category by category.
 *
 * The status is raised on the server and this section never re-decides it: a score that reads
 * above the threshold is stated as such rather than hidden, because the payload and the rows the
 * cockpit holds are refreshed on different beats.
 *
 * Scores are read per offering rather than from the summary's `selectedQuality`: that one follows
 * the cockpit's own category filter, and the status is raised on the offering's whole plan.
 */
export const AttentionStatusFinSobQuality = () => {
  const { openSection } = useCockpitSectionNavigation()
  const { byOffering } = useSelector(selectCockpitFinancialPlanQualitySummary)
  const partnerData = useSelector(selectCockpitPartnerData)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const { role } = useSelector(selectCockpitUserAccess)
  const activeOffering = useAttentionDetailOffering()
  const [isReviewOpen, setIsReviewOpen] = useState(false)

  const partner = partnerData[0]
  const partnerId = partner?.PartnerId ?? ''
  const quality = activeOffering ? byOffering.get(activeOffering) : undefined

  /* Each pillar states the unit its figures are in — percent for revenue growth, percentage points
     for SOB — so the columns are set from the payload rather than from a second copy of that fact
     kept here. */
  const financialUnit = quality?.financialGrowth.valueUnit ?? 'percent'
  const sobUnit = quality?.sobGrowth.valueUnit ?? 'percentagePoints'

  const financialColumns = useMemo(() => getCategoryColumns(financialUnit), [financialUnit])
  const sobColumns = useMemo(() => getCategoryColumns(sobUnit), [sobUnit])

  /* The cockpit sits on the partner's own page, so the way to the FinSob plan is that page's
     financial tab, reached the way every other cockpit row reaches a section: the tab first, then
     the scroll to the strip it lives on. */
  const exitDetail = useAttentionDetailExit()

  const handleOpenSection = useCallback(() => {
    scrollToElement(p360TabsIds)
    openSection({ [TAB_URL]: FINANCIAL_SECTION_URL_VALUE })
    exitDetail()
  }, [exitDetail, openSection])

  const handleOpenReview = useCallback(() => setIsReviewOpen(true), [])
  const handleCloseReview = useCallback(() => setIsReviewOpen(false), [])

  /* Continuing out of the review is the sheet's `adjust the plan` half: the justifications stay
     in the dialog, and the plan itself is changed in the section the review was reading. */
  const handleContinueReview = useCallback(() => {
    setIsReviewOpen(false)
    handleOpenSection()
  }, [handleOpenSection])

  /* The section is stated per offering, and the cockpit's filters decide which offerings a partner
     shows: with none of them left there is nothing to score. */
  if (!activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={DETAIL_KEYS.noOffering} />
        </p>
      </div>
    )
  }

  /* A category scores only where it carries both a baseline and a market growth target; a pillar
     with none of those averages to a bare `0`, which would read as the worst plan there is rather
     than as a plan nothing could be measured on. The plate is therefore drawn only once some
     category behind the score is actually scored. */
  const hasScoredCategories = Boolean(
    quality &&
      [...quality.financialGrowth.categories, ...quality.sobGrowth.categories].some(
        ({ points }) => points !== null
      )
  )
  const score = quality?.overall.score ?? null
  const isAboveThreshold = score !== null && getQualityBand(score) === 'High'
  const gap = score === null ? 0 : Math.max(QUALITY_THRESHOLD - score, 0)
  const meterWidth = Math.min(Math.max(score ?? 0, 0), 100)

  return (
    <div className={styles.root}>
      {!quality || !hasScoredCategories ? (
        <p className={styles.empty}>
          <Translation span text={FIN_SOB_KEYS.notMeasured} />
        </p>
      ) : (
        <>
          {/* The plan's standing on one plate: the score, what it is measured against, and the
          meter with the threshold marked on it, so the gap is seen before it is read. */}
          <div className={styles.plan}>
            <div className={styles.total}>
              <span className={styles.totalValue}>{score}</span>
              <span className={styles.totalLabel}>
                <Translation span text={FIN_SOB_KEYS.score} />
              </span>

              <span
                className={cn(styles.standing, {
                  [styles.standingBelow]: !isAboveThreshold,
                  [styles.standingMet]: isAboveThreshold,
                })}
              >
                <Translation
                  span
                  text={FIN_SOB_KEYS.threshold}
                  replaceParams={[{ key: '{threshold}', value: String(QUALITY_THRESHOLD) }]}
                />
              </span>
            </div>

            <span className={styles.totalTrack}>
              <span
                className={styles.totalFill}
                style={{ transform: `scaleX(${meterWidth / 100})` }}
              />
              <span
                className={styles.totalThreshold}
                style={{ left: `${QUALITY_THRESHOLD}%` }}
                aria-hidden
              />
            </span>

            <p className={styles.standingText}>
              <Translation
                span
                text={getStandingKey(isAboveThreshold, gap)}
                replaceParams={[
                  { key: '{points}', value: String(gap) },
                  { key: '{threshold}', value: String(QUALITY_THRESHOLD) },
                ]}
              />
            </p>
          </div>

          <QualityPillar
            titleKey={KEYS.financial}
            pillar={quality.financialGrowth}
            columns={financialColumns}
            emptyTextKey={FIN_SOB_KEYS.noCategories}
          />

          <QualityPillar
            titleKey={KEYS.sob}
            pillar={quality.sobGrowth}
            columns={sobColumns}
            emptyTextKey={FIN_SOB_KEYS.noCategories}
          />
        </>
      )}

      {/* The review needs a partner to open against; without one the button stays in the bar as
      the reader's right and says it cannot act, rather than disappearing. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.finSobQuality}
        className={styles.actions}
        disabled={partnerId ? [] : ['openQualityReview']}
        handlers={{
          openQualityReview: handleOpenReview,
          goToSection: handleOpenSection,
        }}
      />

      <PlanReviewDialog
        show={isReviewOpen && Boolean(partnerId)}
        partnerId={partnerId}
        fYear={fYear}
        programType={activeOffering}
        isGlobal={partner?.IsGlobal === 1}
        role={PLAN_REVIEW_ROLE_BY_STATUS_ROLE[getAttentionStatusRole(role)]}
        continueCaption={ATTENTION_STATUS_ACTIONS.goToSection.labelKey}
        onClose={handleCloseReview}
        onContinue={handleContinueReview}
        onReject={handleCloseReview}
      />
    </div>
  )
}

AttentionStatusFinSobQuality.displayName = 'AttentionStatusFinSobQuality'
